import React, { Component } from "react";
import { Button, Col, Input, InputGroup, InputGroupAddon, Row } from "reactstrap";
import { toast } from "react-toastify";
import triggerOnPremRemoteCommand from "../../services/remoteControlService";
import {
  getServerByHostname,
  getAllReachableServers,
  getAuthorizedGroups,
  getCloudServerByHostname,
  pingCloudChauffeur,
} from "../../services/serversService";
import userHasRole from "../shared/utils/authUtils";
import { editorRole, viewerRole } from "../shared/constants";
import DisplayWhen from "../common/base/DisplayWhen";

// TODO(Armand): replace by `components/installations/wizard/shared/ServerPicker`
//   (+ `useServerReachability`). Same primitives (search → resolve → ping)
//   but as a functional component with a prop+callback API
//   (`value`, `mode`, `onSelect`, `onReachable`), no toast spam, and an
//   epoch-protected ping that's safe under rapid re-selections.
class SelectServer extends Component {
  state = {
    serverName: "",
    server: {},
    filteredServers: [],
    authorizedGroups: [],
    isDropdownOpen: false,
  };

  async componentDidMount() {
    const { server, mode } = this.props;
    if (server && this.isServerMatchingMode(server, mode)) {
      this.setState({ server, serverName: server.hostname }, () => this.pingServer());
    }

    try {
      const response = await getAuthorizedGroups();
      if (response.status === 200) {
        this.setState({ authorizedGroups: response.data.groups || [] });
      } else {
        toast.error("Failed to fetch authorized groups");
      }
    } catch (ex) {
      this.setState({ authorizedGroups: [] });
    }

    document.addEventListener("click", this.handleClickOutside);
  }

  componentDidUpdate(prevProps) {
    const { server, mode } = this.props;

    if (server && prevProps.server !== server) {
      if (!this.isServerMatchingMode(server, mode)) {
        this.setState({ serverName: "", server: {} });
      } else {
        this.setState({ server, serverName: server.hostname });
      }
    }

    if (!server && prevProps.server) {
      this.setState({ serverName: "", server: {} });
    }
  }

  componentWillUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  }

  isServerMatchingMode = (server, mode) => {
    if (!server) return false;
    const isCloud = server?.group_parent === "cloud";
    return (mode === "cloud" && isCloud) || (mode === "onprem" && !isCloud);
  };

  handleClickOutside = (event) => {
    const { isDropdownOpen } = this.state;

    if (isDropdownOpen && this.dropdownMenu && !this.dropdownMenu.contains(event.target)) {
      this.setState({ isDropdownOpen: false });
    }
  };

  handleSearch = async (value) => {
    const { authorizedGroups } = this.state;
    const { mode } = this.props; // 'onprem' or 'cloud'
    try {
      const response = await getAllReachableServers({
        search: value,
        groups: authorizedGroups,
        mode,
      });
      if (response.status === 200) {
        this.setState({ filteredServers: response.data.results || [], isDropdownOpen: true });
      }
    } catch (ex) {
      this.setState({ filteredServers: [] });
    }
  };

  handleChange = (value) => {
    this.setState({ serverName: value }, () => this.handleSearch(value));
  };

  handleSelectServer = async () => {
    const { serverName } = this.state;
    const { handleReachServer, mode } = this.props;
    const getter = mode === "cloud" ? getCloudServerByHostname : getServerByHostname;
    await getter(serverName)
      .then((response) => {
        if (response.status === 200) {
          toast.success(`Server ${serverName} found, let's try reaching it !`);
          this.setState({ server: response.data });
        } else {
          this.setState({ server: {} });
          toast.error("Server not found...");
          return;
        }
        handleReachServer(response.data, false, mode);
        this.pingServer();
      })
      .catch((ex) => {
        console.log(ex.response);
        toast.error(`Server ${serverName}: ${ex.response.status} ${ex.response.statusText}`);
      });
  };

  handleServerSelect = (serverName) => {
    this.setState({ serverName, filteredServers: [], isDropdownOpen: false });
  };

  async pingServer() {
    const { serverName, server } = this.state;
    const { handleReachServer, mode } = this.props;

    if (!server || Object.keys(server).length === 0) {
      handleReachServer(server, false, mode);
      return;
    }

    if (mode === "cloud") {
      try {
        const { data } = await pingCloudChauffeur(serverName);
        if (data && data.status === "OK") {
          toast.success(`Server ${serverName} is reachable !`);
          handleReachServer(server, true, mode);
        } else {
          toast.error(`Cannot reach ${serverName}...`);
          handleReachServer(server, false, mode);
        }
      } catch (e) {
        toast.error(`Cannot reach ${serverName}...`);
        handleReachServer(server, false, mode);
      }
      return;
    }

    const data = {
      hostname: serverName,
      cmd: "file",
      cmdArgs: {
        path: "/etc/hostname",
      },
      timeout: 5,
    };

    await triggerOnPremRemoteCommand(data)
      .then((response) => {
        if (response.status === "OK") {
          toast.success(`Server ${serverName} is reachable !`);
          handleReachServer(server, true, mode);
        } else {
          toast.error(`Cannot reach ${serverName}...\n${response.stderr}`);
          handleReachServer(server, false, mode);
        }
      })
      .catch(() => {
        toast.error(`Unexpected error for ${serverName}`);
        handleReachServer(server, false, mode);
      });
  }

  render() {
    const { serverName, filteredServers, isDropdownOpen } = this.state;
    const { handleListServersReverseTunnel, handleGenerateImageForNuc, mode } = this.props;

    return (
      <Row>
        <Col md="4">
          <InputGroup>
            <Input
              name="server"
              id={`server-${mode}`}
              value={serverName}
              placeholder={mode === "cloud" ? "Choose a cloud server" : "Choose an on‑premise server"}
              onChange={(e) => this.handleChange(e.currentTarget.value)}
            />
            <InputGroupAddon addonType="append">
              <Button className="input-group-button" onClick={this.handleSelectServer}>
                Select
              </Button>
            </InputGroupAddon>
          </InputGroup>
          {isDropdownOpen && filteredServers && filteredServers.length > 0 && (
            <ul
              className="dropdown-menu show"
              ref={(el) => {
                this.dropdownMenu = el;
              }}
            >
              {filteredServers.map((server) => (
                <li key={server.id} className="dropdown-item" onClick={() => this.handleServerSelect(server.hostname)}>
                  {server.hostname}
                </li>
              ))}
            </ul>
          )}
        </Col>
        <Col md="4" />
        <Col md="4" className="d-flex flex-column align-items-end">
          <DisplayWhen
            condition={(userHasRole(editorRole) || userHasRole(viewerRole)) && !!handleListServersReverseTunnel}
          >
            <Button className="btn-success m-2" onClick={handleListServersReverseTunnel}>
              <i className="fas fa-search mr-2" />
              List Servers Reverse Tunnel
            </Button>
          </DisplayWhen>
          <DisplayWhen condition={(userHasRole(editorRole) || userHasRole(viewerRole)) && !!handleGenerateImageForNuc}>
            <Button className="btn-info m-2" onClick={handleGenerateImageForNuc}>
              <i className="fas fa-compact-disc mr-2" />
              Generate image for NUC
            </Button>
          </DisplayWhen>
        </Col>
      </Row>
    );
  }
}

export default SelectServer;
